import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const CONSENT_STORAGE_KEY = 'site-consent-preferences';
const CONSENT_VERSION = 1;

const defaultPreferences = {
  analytics: false,
  externalMedia: false,
};

const ConsentContext = createContext({
  preferences: defaultPreferences,
  hasDecision: false,
  isPreferencesOpen: false,
  acceptAll: () => {},
  rejectNonEssential: () => {},
  savePreferences: () => {},
  grantCategory: () => {},
  openPreferences: () => {},
  closePreferences: () => {},
  canUse: () => false,
});

function normalizePreferences(value = {}) {
  return {
    analytics: Boolean(value.analytics),
    externalMedia: Boolean(value.externalMedia),
  };
}

function loadStoredConsent() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(CONSENT_STORAGE_KEY);

    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);

    if (!parsedValue || parsedValue.version !== CONSENT_VERSION) {
      return null;
    }

    return {
      version: CONSENT_VERSION,
      consentedAt: parsedValue.consentedAt || '',
      preferences: normalizePreferences(parsedValue.preferences),
    };
  } catch {
    return null;
  }
}

function persistConsent(record) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
}

export function ConsentProvider({ children }) {
  const [consentRecord, setConsentRecord] = useState(() => loadStoredConsent());
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const preferences = consentRecord?.preferences || defaultPreferences;
  const hasDecision = Boolean(consentRecord);

  const savePreferences = useCallback((nextPreferences) => {
    const record = {
      version: CONSENT_VERSION,
      consentedAt: new Date().toISOString(),
      preferences: normalizePreferences(nextPreferences),
    };

    persistConsent(record);
    setConsentRecord(record);
    setIsPreferencesOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    savePreferences({
      analytics: true,
      externalMedia: true,
    });
  }, [savePreferences]);

  const rejectNonEssential = useCallback(() => {
    savePreferences(defaultPreferences);
  }, [savePreferences]);

  const grantCategory = useCallback((category) => {
    if (!Object.prototype.hasOwnProperty.call(defaultPreferences, category)) {
      return;
    }

    savePreferences({
      ...preferences,
      [category]: true,
    });
  }, [preferences, savePreferences]);

  const openPreferences = useCallback(() => setIsPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setIsPreferencesOpen(false), []);
  const canUse = useCallback((category) => Boolean(preferences?.[category]), [preferences]);

  const value = useMemo(() => ({
    preferences,
    hasDecision,
    isPreferencesOpen,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    grantCategory,
    openPreferences,
    closePreferences,
    canUse,
  }), [
    acceptAll,
    canUse,
    closePreferences,
    grantCategory,
    hasDecision,
    isPreferencesOpen,
    openPreferences,
    preferences,
    rejectNonEssential,
    savePreferences,
  ]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  return useContext(ConsentContext);
}
